import { useState, useEffect, useRef } from "react";
import {
  Radar, TrendingUp, TrendingDown, Minus, Flame, Snowflake,
  Zap, ChevronRight, ArrowUpRight, ArrowDownRight, Activity
} from "lucide-react";
import { API } from "../../lib/constants";
import { usePrefetchOrFetch } from "../common/DataPrefetch";
import { Shimmer } from "../common/DashboardSkeleton";

const Reveal = ({ children, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50 + delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)", transition: `all 0.5s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

const STATUS_CONFIG = {
  emerging: { color: "#22c55e", bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.25)", icon: Flame, label: "Emerging" },
  rising:   { color: "#3b82f6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.25)", icon: TrendingUp, label: "Rising" },
  stable:   { color: "#a855f7", bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.25)", icon: Minus, label: "Stable" },
  cooling:  { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.25)", icon: TrendingDown, label: "Cooling" },
  declining:{ color: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.25)", icon: Snowflake, label: "Declining" },
};

const Sparkline = ({ data, color, height = 28 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map(d => d.freq), 0.1);
  const w = 80;
  const points = data.map((d, i) => `${(i / (data.length - 1)) * w},${height - (d.freq / max) * (height - 4)}`).join(" ");
  return (
    <svg width={w} height={height} className="flex-shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
      {data.map((d, i) => (
        <circle key={`sp-${d.freq}-${i}`} cx={(i / (data.length - 1)) * w} cy={height - (d.freq / max) * (height - 4)} r="2" fill={color} opacity={i === data.length - 1 ? 1 : 0.4} />
      ))}
    </svg>
  );
};

const RadarViz = ({ topics }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !topics.length) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;
    const maxR = Math.min(cx, cy) - 40;

    ctx.clearRect(0, 0, W, H);

    // Draw concentric rings
    for (let i = 1; i <= 4; i++) {
      const r = (i / 4) * maxR;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,255,255,${0.04 + i * 0.01})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // Draw radial lines
    const n = Math.min(topics.length, 16);
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // Normalize momentum for radius
    const maxMom = Math.max(...topics.slice(0, n).map(t => Math.abs(t.momentum)), 1);

    // Draw topic nodes
    const displayed = topics.slice(0, n);
    const points = [];
    displayed.forEach((t, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const r = Math.max(0.15, Math.min(Math.abs(t.momentum) / maxMom, 1)) * maxR;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.stable;
      points.push({ x, y });

      // Node glow
      const grad = ctx.createRadialGradient(x, y, 0, x, y, 16);
      grad.addColorStop(0, cfg.color + "44");
      grad.addColorStop(1, cfg.color + "00");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, 16, 0, Math.PI * 2);
      ctx.fill();

      // Node dot
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = cfg.color;
      ctx.fill();

      // Label
      const labelX = cx + Math.cos(angle) * (r + 20);
      const labelY = cy + Math.sin(angle) * (r + 20);
      ctx.fillStyle = "#cbd5e1";
      ctx.font = "9px Inter, system-ui, sans-serif";
      ctx.textAlign = angle > Math.PI / 2 || angle < -Math.PI / 2 ? "right" : "left";
      ctx.textBaseline = "middle";
      ctx.fillText(t.topic, labelX, labelY);
    });

    // Connect polygon
    if (points.length > 2) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.closePath();
      ctx.fillStyle = "rgba(34,197,94,0.04)";
      ctx.fill();
      ctx.strokeStyle = "rgba(34,197,94,0.15)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Center label
    ctx.fillStyle = "#94a3b8";
    ctx.font = "10px Inter, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("RADAR", cx, cy);
  }, [topics]);

  return <canvas ref={canvasRef} width={400} height={400} className="w-full max-w-[400px] mx-auto" />;
};

const TopicRow = ({ topic, idx }) => {
  const cfg = STATUS_CONFIG[topic.status] || STATUS_CONFIG.stable;
  const Icon = cfg.icon;
  const MomIcon = topic.momentum > 0 ? ArrowUpRight : topic.momentum < -10 ? ArrowDownRight : Minus;

  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg border border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`trend-topic-${idx}`}>
      <span className="text-[9px] text-slate-600 w-5 text-right font-mono">{idx + 1}</span>
      <div className="p-1 rounded" style={{ background: cfg.bg }}>
        <Icon className="w-3 h-3" style={{ color: cfg.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white capitalize truncate">{topic.topic}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
            {cfg.label}
          </span>
          <span className="text-[9px] text-slate-500">{topic.total_count} papers</span>
          <span className="text-[9px] text-slate-600">peak {topic.peak_year}</span>
        </div>
      </div>
      <Sparkline data={topic.timeline} color={cfg.color} />
      <div className="flex items-center gap-1 w-20 justify-end">
        <MomIcon className="w-3 h-3" style={{ color: topic.momentum > 0 ? "#22c55e" : topic.momentum < -10 ? "#ef4444" : "#94a3b8" }} />
        <span className="text-xs font-mono font-bold" style={{ color: topic.momentum > 0 ? "#22c55e" : topic.momentum < -10 ? "#ef4444" : "#94a3b8" }}>
          {topic.momentum > 0 ? "+" : ""}{topic.momentum > 999 ? `${(topic.momentum / 1000).toFixed(1)}K` : topic.momentum.toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

export const ResearchTrendsRadar = () => {
  const [data, loading] = usePrefetchOrFetch(
    "researchTrendsRadar",
    `${API}/research/trends-radar`,
    {
      validate: (d) => d && Array.isArray(d.emerging) && Array.isArray(d.rising),
      delay: 500,
    }
  );
  const [activeTab, setActiveTab] = useState("emerging");

  if (loading) {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="trends-radar-loading">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-3">
          <Shimmer className="h-3 w-44" />
          <Shimmer className="h-6 w-64" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-2">
              <Shimmer className="h-3 w-24" />
              <Shimmer className="h-5 w-16" />
              <Shimmer className="h-2 w-full rounded-full" />
            </div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-500 animate-pulse">Analyzing research trends...</p>
      </div>
    );
  }

  if (!data) return <div className="text-slate-500 text-center py-10">Unable to load trends data.</div>;

  const tabs = [
    { id: "emerging", label: "Emerging", count: data.emerging?.length, color: "#22c55e" },
    { id: "rising", label: "Rising", count: data.rising?.length, color: "#3b82f6" },
    { id: "stable", label: "Dominant", count: data.stable?.length, color: "#a855f7" },
    { id: "declining", label: "Declining", count: data.declining?.length, color: "#ef4444" },
  ];

  const activeTopics = data[activeTab] || [];

  return (
    <div className="space-y-6" data-testid="research-trends-radar">
      {/* Header */}
      <Reveal>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: "rgba(34,197,94,0.15)" }}>
            <Radar className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Research Trends Radar</h2>
            <p className="text-[10px] text-slate-500">
              Detecting emerging themes across {data.total_topics_analyzed?.toLocaleString()} topics from {data.years?.[0]}–{data.years?.[data.years?.length - 1]}
            </p>
          </div>
        </div>
      </Reveal>

      {/* Radar Visualization + Top Emerging */}
      <Reveal delay={80}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-white">Momentum Radar</h3>
            </div>
            <RadarViz topics={data.emerging || []} />
            <p className="text-[9px] text-slate-600 text-center mt-1">Distance from center = momentum strength</p>
          </div>

          {/* Hot Signals */}
          <div className="p-4 rounded-lg border border-emerald-500/10 bg-emerald-500/[0.02]">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-white">Hot Signals</h3>
              <span className="text-[9px] text-emerald-400/70 ml-auto">fastest growing themes</span>
            </div>
            <div className="space-y-2">
              {(data.emerging || []).slice(0, 8).map((t, i) => (
                <div key={t.topic || i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                  <span className="text-emerald-400 text-[10px] font-mono w-5">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white capitalize truncate font-medium">{t.topic}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] text-slate-500">{t.total_count} papers</span>
                      <span className="text-[9px] text-slate-600">peak {t.peak_year}</span>
                    </div>
                  </div>
                  <Sparkline data={t.timeline} color="#22c55e" height={24} />
                  <div className="flex items-center gap-0.5">
                    <ArrowUpRight className="w-3 h-3 text-emerald-400" />
                    <span className="text-[10px] font-mono font-bold text-emerald-400">
                      {t.momentum > 999 ? `${(t.momentum / 1000).toFixed(1)}K` : t.momentum.toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* Year Volume Context */}
      <Reveal delay={160}>
        <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-3">
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-white">Publication Volume</h3>
          </div>
          <div className="flex items-end gap-2 h-20">
            {(data.years || []).map(yr => {
              const count = data.year_totals?.[String(yr)] || 0;
              const maxCount = Math.max(...Object.values(data.year_totals || {}).map(Number), 1);
              const pct = (count / maxCount) * 100;
              return (
                <div key={yr} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] text-slate-400 font-mono">{count.toLocaleString()}</span>
                  <div className="w-full rounded-t" style={{ height: `${pct}%`, background: "linear-gradient(to top, rgba(99,102,241,0.3), rgba(99,102,241,0.6))", minHeight: "4px" }} />
                  <span className="text-[9px] text-slate-500">{yr}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Tab Navigation */}
      <Reveal delay={240}>
        <div className="flex gap-2 border-b border-white/5 pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? "text-white" : "text-slate-500 hover:text-slate-300"}`}
              style={activeTab === tab.id ? { background: STATUS_CONFIG[tab.id]?.bg, border: `1px solid ${STATUS_CONFIG[tab.id]?.border}`, color: tab.color } : {}}
              data-testid={`trends-tab-${tab.id}`}>
              {tab.label} {tab.count > 0 && <span className="ml-1 opacity-60">({tab.count})</span>}
            </button>
          ))}
        </div>
      </Reveal>

      {/* Topic List */}
      <Reveal delay={320}>
        <div className="space-y-1.5">
          {activeTopics.map((t, i) => (
            <TopicRow key={t.topic} topic={t} idx={i} />
          ))}
          {activeTopics.length === 0 && (
            <p className="text-slate-500 text-sm text-center py-6">No topics in this category.</p>
          )}
        </div>
      </Reveal>

      {/* Top Movers */}
      <Reveal delay={400}>
        <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-indigo-400" />
            <h3 className="text-sm font-semibold text-white">Top 10 Movers</h3>
            <span className="text-[9px] text-slate-500 ml-auto">highest absolute momentum shift</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(data.top_movers || []).slice(0, 10).map((t, i) => {
              const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.stable;
              return (
                <div key={t.topic || i} className="flex items-center gap-2 py-1.5 px-2 rounded border border-white/5">
                  <div className="w-1.5 h-6 rounded-full" style={{ background: cfg.color }} />
                  <span className="text-xs text-white capitalize flex-1 truncate">{t.topic}</span>
                  <Sparkline data={t.timeline} color={cfg.color} height={20} />
                  <span className="text-[10px] font-mono font-bold" style={{ color: cfg.color }}>
                    {t.momentum > 0 ? "+" : ""}{t.momentum > 999 ? `${(t.momentum / 1000).toFixed(1)}K` : t.momentum.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Footer */}
      <Reveal delay={480}>
        <div className="text-center py-2">
          <p className="text-[9px] text-slate-600">
            Momentum calculated by comparing normalized bigram frequency between {data.years?.[data.years?.length - 2]}–{data.years?.[data.years?.length - 1]} vs {data.years?.[0]}–{data.years?.[1]} periods.
            Analyzed {data.total_topics_analyzed?.toLocaleString()} unique research themes.
          </p>
        </div>
      </Reveal>
    </div>
  );
};
